import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, BookOpen, ClipboardCheck, MessageCircle, Check, ChevronLeft, ArrowRight, Coffee, Loader2, Crown, LayoutDashboard } from "lucide-react";
import VocabularyChips from "@/components/VocabularyChips";
import TrackHeroImage from "@/components/TrackHeroImage";
import { useAuth } from "@/context/AuthContext";

// Pass threshold for Wednesday assessments — must mirror PASS_THRESHOLD in backend/server.py.
const ASSESSMENT_PASS_PCT = 70;

export default function TrackDetail() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialSession = ["monday", "wednesday", "friday"].includes(searchParams.get("start"))
    ? searchParams.get("start")
    : "monday";
  const [track, setTrack] = useState(null);
  const [journals, setJournals] = useState([]);
  const [progress, setProgress] = useState({});
  const [allProgress, setAllProgress] = useState([]);
  const [assessment, setAssessment] = useState(null);
  const [allTracks, setAllTracks] = useState([]);
  const [activeSession, setActiveSession] = useState(initialSession);

  useEffect(() => {
    (async () => {
      const [t, j, p, a, all] = await Promise.all([
        api.get(`/tracks/${slug}`),
        api.get(`/journal?track_slug=${slug}`),
        api.get(`/progress`),
        api.get(`/assessments`),
        api.get(`/tracks`),
      ]);
      setTrack(t.data);
      setJournals(j.data);
      const prog = p.data.find((x) => x.track_slug === slug) || {};
      setProgress(prog);
      setAllProgress(p.data);
      const a_for_track = a.data.find((x) => x.track_slug === slug);
      setAssessment(a_for_track || null);
      setAllTracks(all.data);
    })();
    // Only re-run when the visited track changes — setters are stable refs
    // and `api` is a module-level singleton.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const markComplete = async (session) => {
    const { data } = await api.post("/progress", { track_slug: slug, session, completed: true });
    setProgress(data);
    // Keep allProgress in sync so the header counter updates live.
    setAllProgress((prev) => {
      const others = prev.filter((p) => p.track_slug !== slug);
      return [...others, data];
    });
    toast.success(`${session.charAt(0).toUpperCase() + session.slice(1)} session complete.`);
  };

  // Forward-flow helpers. Each session knows what comes next.
  const goNextSession = () => {
    if (activeSession === "monday") setActiveSession("wednesday");
    else if (activeSession === "wednesday") setActiveSession("friday");
    else goNextTrack();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const goNextTrack = () => {
    const idx = allTracks.findIndex((t) => t.slug === slug);
    const next = allTracks[idx + 1];
    if (next) navigate(`/tracks/${next.slug}?start=monday`);
    else navigate("/dashboard");
  };
  const takeBreak = () => navigate("/dashboard");
  const isLastTrack = () => {
    const idx = allTracks.findIndex((t) => t.slug === slug);
    return idx >= 0 && idx === allTracks.length - 1;
  };

  if (!track) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#8A8782]">Loading…</div>
    );
  }

  // Count tracks fully complete (Mon + Wed + Fri all true) for the Path-to-Fellow indicator.
  const completedTracksCount = allProgress.filter(
    (p) => p.monday && p.wednesday && p.friday,
  ).length;
  const totalTracks = allTracks.length || 7;
  const fellowReached = completedTracksCount >= totalTracks && totalTracks > 0;

  return (
    <div className="bg-tcpi-bg" data-testid="track-detail-page">
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-12">
        <Link to="/tracks" className="inline-flex items-center gap-1 text-sm text-[#8A8782] hover:text-[#C9A961] mb-6" data-testid="back-to-tracks">
          <ChevronLeft className="w-4 h-4" /> All tracks
        </Link>

        {/* Path-to-Crown-Fellow progress — visible at the top of every track. */}
        <Link
          to="/certificate"
          className="group block mb-6 rounded-md border border-[#C9A961]/30 bg-[#C9A961]/5 hover:bg-[#C9A961]/10 hover:border-[#C9A961]/60 transition px-4 py-3"
          data-testid="path-to-fellow-badge"
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <Crown className="w-5 h-5 text-[#C9A961] shrink-0" strokeWidth={1.4} />
              <div className="text-sm text-[#D1CFC9]">
                <span className="text-[#C9A961] font-semibold" data-testid="path-completed-count">
                  {completedTracksCount}
                </span>
                <span className="text-[#8A8782]"> / {totalTracks}</span>{" "}
                <span className="text-[#F7F5F0]">tracks on the way to</span>{" "}
                <span className="text-[#C9A961]">Crown Fellow</span>
              </div>
            </div>
            <div className="flex items-center gap-3 min-w-[140px]">
              <div className="h-1.5 w-32 sm:w-40 bg-[#1A1816] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#C9A961] transition-all duration-500"
                  style={{ width: `${Math.min(100, (completedTracksCount / totalTracks) * 100)}%` }}
                />
              </div>
              <span className="text-[10px] uppercase tracking-wider text-[#8A8782] group-hover:text-[#C9A961] transition">
                {fellowReached ? "Fellow ✓" : "View certificate →"}
              </span>
            </div>
          </div>
        </Link>

        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="overline mb-3">Track {track.number}</div>
            <h1
              className="text-4xl md:text-5xl text-[#F7F5F0] mb-3"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
            >
              {track.title}
            </h1>
            <div className="text-[#C9A961] mb-2 tracking-wide">
              {track.course_code} · "{track.course_title}"
            </div>
            <p className="text-[#D1CFC9] max-w-3xl">{track.purpose}</p>
          </div>
          <SessionProgressRing progress={progress} />
        </div>

        {/* Session tabs */}
        <div className="mt-10 grid grid-cols-3 gap-3 max-w-3xl">
          {[
            { id: "monday", label: "Mon", title: "Learn & Reflect", icon: BookOpen },
            { id: "wednesday", label: "Wed", title: "Apply & Assess", icon: ClipboardCheck },
            { id: "friday", label: "Fri", title: "Share & Lead", icon: MessageCircle },
          ].map(({ id, label, title, icon: Icon }) => {
            const done = progress[id];
            const active = activeSession === id;
            return (
              <button
                key={id}
                onClick={() => setActiveSession(id)}
                className={`text-left p-4 rounded-md border transition ${
                  active
                    ? "border-[#C9A961] bg-[#C9A961]/10"
                    : "border-[#C9A961]/20 hover:border-[#C9A961]/50"
                }`}
                data-testid={`session-tab-${id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <Icon className="w-5 h-5 text-[#C9A961]" strokeWidth={1.5} />
                  {done && <Check className="w-4 h-4 text-[#C9A961]" />}
                </div>
                <div className="overline text-[10px]">{label}</div>
                <div className="text-[#F7F5F0]">{title}</div>
              </button>
            );
          })}
        </div>

        {/* Active session content */}
        <div className="mt-10 pb-28">
          {activeSession === "monday" && (
            <MondaySession
              track={track}
              journals={journals}
              setJournals={setJournals}
              onComplete={() => markComplete("monday")}
              done={progress.monday}
              onAdvance={goNextSession}
              onBreak={takeBreak}
            />
          )}
          {activeSession === "wednesday" && (
            <WednesdaySession
              track={track}
              assessment={assessment}
              onSubmit={(a) => setAssessment(a)}
              onComplete={() => markComplete("wednesday")}
              done={progress.wednesday}
              onAdvance={goNextSession}
              onBreak={takeBreak}
            />
          )}
          {activeSession === "friday" && (
            <FridaySession
              track={track}
              onComplete={() => markComplete("friday")}
              done={progress.friday}
              onAdvance={goNextTrack}
              onBreak={takeBreak}
              isLastTrack={isLastTrack()}
            />
          )}
        </div>
      </section>
    </div>
  );
}

/* ---------- Shared: session-progress ring ---------- */
function SessionProgressRing({ progress }) {
  const sessions = ["monday", "wednesday", "friday"];
  const done = sessions.filter((s) => progress?.[s]).length;
  const pct = (done / sessions.length) * 100;
  // SVG ring math
  const r = 36;
  const c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;
  const complete = done === sessions.length;
  return (
    <div
      className="shrink-0 flex flex-col items-center gap-2"
      data-testid="session-progress-ring"
      aria-label={`${done} of ${sessions.length} weekly sessions complete`}
    >
      <div className="relative w-[92px] h-[92px]">
        <svg width="92" height="92" viewBox="0 0 92 92" className="absolute inset-0">
          <circle
            cx="46"
            cy="46"
            r={r}
            fill="none"
            stroke="#C9A961"
            strokeOpacity="0.18"
            strokeWidth="6"
          />
          <circle
            cx="46"
            cy="46"
            r={r}
            fill="none"
            stroke="#C9A961"
            strokeWidth="6"
            strokeDasharray={c}
            strokeDashoffset={offset}
            strokeLinecap="round"
            transform="rotate(-90 46 46)"
            style={{ transition: "stroke-dashoffset 600ms ease" }}
          />
        </svg>
        <div
          className="absolute inset-0 flex items-center justify-center text-[#F7F5F0] text-2xl"
          style={{ fontFamily: "Cormorant Garamond, serif" }}
        >
          {done}<span className="text-[#8A8782] mx-0.5">/</span>{sessions.length}
        </div>
      </div>
      <div className="text-[10px] uppercase tracking-wider text-[#8A8782] text-center leading-tight">
        {complete ? "Track complete" : "Sessions this week"}
      </div>
    </div>
  );
}


/* ---------- Shared: sticky next-action bar ---------- */
function NextActionBar({ label, onContinue, onBreak, continueLabel = "Continue", breakLabel = "Take a break" }) {
  return (
    <div
      className="fixed bottom-0 inset-x-0 z-30 border-t border-[#C9A961]/30 bg-[#0A0908]/95 backdrop-blur"
      data-testid="next-action-bar"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-3 flex items-center justify-between gap-3">
        <span className="text-sm text-[#D1CFC9] hidden sm:inline">{label}</span>
        <span className="text-xs text-[#D1CFC9] sm:hidden">{label}</span>
        <div className="flex items-center gap-2 ml-auto">
          <button
            onClick={onBreak}
            className="btn-secondary text-xs sm:text-sm inline-flex items-center gap-1.5"
            data-testid="break-btn"
          >
            <Coffee className="w-3.5 h-3.5" /> {breakLabel}
          </button>
          <button
            onClick={onContinue}
            className="btn-primary text-xs sm:text-sm inline-flex items-center gap-1.5"
            data-testid="continue-btn"
          >
            {continueLabel} <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}


/* ---------- Shared: persistent save-and-break bar ---------- */
function SaveBreakBar({ label, onBreak }) {
  return (
    <div
      className="fixed bottom-0 inset-x-0 z-30 border-t border-[#C9A961]/20 bg-[#0A0908]/95 backdrop-blur"
      data-testid="save-break-bar"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-2.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Check className="w-3.5 h-3.5 text-[#C9A961] shrink-0" />
          <span className="text-[11px] sm:text-xs text-[#8A8782] truncate">
            {label} <span className="text-[#C9A961]">Progress is saved.</span>
          </span>
        </div>
        <button
          onClick={onBreak}
          className="btn-secondary text-xs inline-flex items-center gap-1.5 shrink-0"
          data-testid="save-break-btn"
        >
          <Coffee className="w-3.5 h-3.5" /> Save &amp; take a break
        </button>
      </div>
    </div>
  );
}


/* ---------- Monday: Learn & Reflect ---------- */
function computeMentorTurns(entry) {
  if (entry?.ai_turns?.length) return entry.ai_turns;
  if (entry?.ai_feedback) return [{ role: "mentor", text: entry.ai_feedback }];
  return [];
}

function MondaySession({ track, journals, setJournals, onComplete, done, onAdvance, onBreak }) {
  const { user } = useAuth();
  const [currentPrompt, setCurrentPrompt] = useState(0);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(null);
  const [replyDraft, setReplyDraft] = useState("");
  const [replying, setReplying] = useState(false);
  const journalCardRef = useRef(null);

  const promptText = track.journal_prompts[currentPrompt];
  const existing = journals.find(
    (j) => j.track_slug === track.slug && j.prompt_index === currentPrompt
  );

  // All-prompts-complete sentinel — drives the end-of-section panel.
  const completedCount = track.journal_prompts.filter((_, i) =>
    journals.some((j) => j.track_slug === track.slug && j.prompt_index === i && j.adequate !== false)
  ).length;
  const allDone = completedCount >= track.journal_prompts.length;

  useEffect(() => {
    setDraft(existing?.content || "");
    setReplyDraft("");
    // We intentionally re-seed the draft only when the participant switches
    // prompts or the underlying entry id changes — NOT when `existing.content`
    // mutates, otherwise every save would clobber the in-progress draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPrompt, existing?.id]);

  // Mark Monday complete the moment all five prompts have qualified answers.
  useEffect(() => {
    if (allDone && !done) onComplete();
  }, [allDone, done, onComplete]);

  const advanceToNextPrompt = () => {
    const remaining = track.journal_prompts.findIndex((_, i) =>
      i > currentPrompt &&
      !journals.some((j) => j.track_slug === track.slug && j.prompt_index === i && j.adequate !== false)
    );
    if (remaining >= 0) {
      setCurrentPrompt(remaining);
      requestAnimationFrame(() => {
        journalCardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }
  };

  const save = async () => {
    if (!draft.trim()) return;
    setSaving(true);
    try {
      let result;
      if (existing) {
        const { data } = await api.put(`/journal/${existing.id}`, { content: draft });
        setJournals(journals.map((j) => (j.id === data.id ? data : j)));
        result = data;
      } else {
        const { data } = await api.post("/journal", {
          track_slug: track.slug,
          prompt_index: currentPrompt,
          prompt: promptText,
          content: draft,
        });
        setJournals([data, ...journals]);
        result = data;
      }
      // Auto-advance only when the entry actually engages the prompt.
      if (result.adequate !== false && result.on_topic !== false) {
        toast.success("Reflection saved.");
        // Do NOT auto-advance — give the mentor a chance to respond if the
        // participant wants it. They'll click "Continue" or "Mentor" below.
      } else {
        toast.warning("Saved — but it doesn't seem to engage the prompt yet.");
      }
    } catch (e) {
      toast.error("Could not save reflection.");
    } finally {
      setSaving(false);
    }
  };

  const requestFeedback = async (id) => {
    setAiLoading(id);
    try {
      const { data } = await api.post(`/journal/${id}/ai-feedback`);
      setJournals(journals.map((j) => (j.id === id ? data : j)));
      toast.success("Mentor responded.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI feedback failed.");
    } finally {
      setAiLoading(null);
    }
  };

  const sendReply = async (id) => {
    if (!replyDraft.trim()) return;
    setReplying(true);
    try {
      const { data } = await api.post(`/journal/${id}/ai-reply`, { text: replyDraft });
      setJournals(journals.map((j) => (j.id === id ? data : j)));
      setReplyDraft("");
      toast.success("Mentor responded.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send reply.");
    } finally {
      setReplying(false);
    }
  };

  // Chat-style turns for the current prompt's entry.
  const turns = computeMentorTurns(existing);
  const mentorTurnCount = turns.filter((t) => t.role === "mentor").length;
  const canReplyToMentor = mentorTurnCount === 1; // exactly one mentor message, no participant reply yet

  return (
    <div className="grid lg:grid-cols-5 gap-10" data-testid="monday-session">
      {/* Reading */}
      <div className="lg:col-span-3">
        <TrackHeroImage slug={track.slug} program={user?.program} title={track.title} />
        <div className="overline mb-3">Core Reading</div>
        <h2
          className="text-3xl text-[#F7F5F0] mb-6"
          style={{ fontFamily: "Cormorant Garamond, serif" }}
        >
          {track.course_title}
        </h2>
        <article className="prose-tcpi">
          {track.reading.split("\n\n").map((para, i) => (
            <p key={`para-${i}-${para.slice(0, 20)}`}>{para}</p>
          ))}
        </article>

        <div className="mt-10 card-bento">
          <div className="overline mb-2">Track Vocabulary</div>
          <p className="text-xs text-[#8A8782] mb-3 italic">Tap a term to read its definition and source.</p>
          <VocabularyChips terms={track.vocabulary} />
        </div>

        {Array.isArray(track.citations) && track.citations.length > 0 && (
          <div className="mt-6 card-bento" data-testid="track-citations">
            <div className="overline mb-3">Sources & Further Reading</div>
            <p className="text-xs text-[#8A8782] mb-4 italic">
              Every claim in this reading is grounded in published, verifiable research. Use this list to go
              deeper, to bring to a facilitator, or to share with someone you mentor.
            </p>
            <ol className="space-y-3 text-sm text-[#D1CFC9] list-decimal pl-5">
              {track.citations.map((c, i) => (
                <li key={`cite-${i}-${c.year}-${(c.title || '').slice(0, 24)}`}>
                  <span className="text-[#F7F5F0]">{c.author}</span>{c.year ? ` (${c.year}). ` : ". "}
                  <em className="text-[#C9A961]">{c.title}</em>
                  {c.publisher ? <span className="text-[#8A8782]">. {c.publisher}.</span> : null}
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>

      {/* Journal */}
      <div className="lg:col-span-2">
        <div ref={journalCardRef} className="card-bento sticky top-24">
          <div className="flex items-center justify-between mb-3">
            <div className="overline">Reflection Journal</div>
            <div className="text-[10px] text-[#8A8782]" data-testid="journal-progress">
              {completedCount} / {track.journal_prompts.length}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mb-5">
            {track.journal_prompts.map((promptText, i) => {
              const entry = journals.find((j) => j.track_slug === track.slug && j.prompt_index === i);
              const qualified = entry && entry.adequate !== false;
              const isActive = currentPrompt === i;
              let pillClass;
              if (isActive) pillClass = "bg-[#C9A961] text-[#0A0908] border-[#C9A961]";
              else if (qualified) pillClass = "border-[#C9A961] text-[#C9A961]";
              else pillClass = "border-[#C9A961]/25 text-[#8A8782]";
              return (
                <button
                  key={`prompt-${i}-${promptText.slice(0, 16)}`}
                  onClick={() => setCurrentPrompt(i)}
                  className={`w-9 h-9 rounded-full text-sm border transition ${pillClass}`}
                  data-testid={`prompt-${i}`}
                >
                  {qualified ? <Check className="w-3.5 h-3.5 mx-auto" /> : i + 1}
                </button>
              );
            })}
          </div>

          <div
            className="text-lg text-[#F7F5F0] mb-4 leading-snug"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            {promptText}
          </div>

          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={6}
            placeholder="A sentence or two is fine — just answer the question honestly."
            className="input-tcpi resize-none mb-3"
            data-testid="journal-textarea"
          />

          <div className="flex gap-2">
            <button
              onClick={save}
              disabled={saving || !draft.trim()}
              className="btn-primary text-sm flex-1 disabled:opacity-50"
              data-testid="journal-save-btn"
            >
              {(() => {
                if (saving) return "Saving…";
                return existing ? "Update" : "Save";
              })()}
            </button>
            {existing && mentorTurnCount === 0 && (
              <button
                onClick={() => requestFeedback(existing.id)}
                disabled={aiLoading === existing.id}
                className="btn-secondary text-sm flex items-center gap-1 gold-pulse"
                data-testid="journal-ai-btn"
              >
                {aiLoading === existing.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}
                Mentor
              </button>
            )}
          </div>

          {existing && existing.adequate === false && existing.quality_feedback && (
            <div className="mt-5 p-3 bg-[#1A1816] border border-amber-500/40 rounded-md" data-testid="journal-quality-feedback">
              <div className="flex items-center gap-2 mb-1 text-amber-400 text-xs uppercase tracking-wider">
                <Sparkles className="w-3 h-3" /> Try again
              </div>
              <p className="text-xs text-[#D1CFC9] leading-relaxed">
                {existing.quality_feedback}
              </p>
            </div>
          )}

          {/* Mentor chat — up to 2 mentor turns + 1 participant reply */}
          {turns.length > 0 && (
            <div className="mt-5 space-y-3" data-testid="mentor-chat">
              {turns.map((t, i) => (
                <div
                  key={`turn-${i}`}
                  className={`p-3 rounded-md border text-sm leading-relaxed ${
                    t.role === "mentor"
                      ? "bg-[#1A1816] border-[#C9A961]/30 text-[#D1CFC9]"
                      : "bg-[#C9A961]/10 border-[#C9A961]/40 text-[#F7F5F0]"
                  }`}
                  data-testid={`mentor-turn-${i}`}
                >
                  <div className={`overline text-[10px] mb-1 ${t.role === "mentor" ? "text-[#C9A961]" : "text-[#8A8782]"}`}>
                    {t.role === "mentor" ? "Mentor" : "You"}
                  </div>
                  <p className="whitespace-pre-wrap">{t.text}</p>
                </div>
              ))}

              {canReplyToMentor && existing && (
                <div className="pt-1">
                  <textarea
                    value={replyDraft}
                    onChange={(e) => setReplyDraft(e.target.value)}
                    rows={2}
                    placeholder="One reply allowed — ask a follow-up or share what landed."
                    className="input-tcpi resize-none text-sm"
                    data-testid="mentor-reply-input"
                  />
                  <button
                    onClick={() => sendReply(existing.id)}
                    disabled={replying || !replyDraft.trim()}
                    className="btn-secondary text-xs mt-2 inline-flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="mentor-reply-btn"
                  >
                    {replying ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                    Send reply
                  </button>
                </div>
              )}

              {mentorTurnCount === 2 && (
                <p className="text-[11px] text-[#8A8782] italic text-center pt-1">
                  That's the end of this exchange. Use what landed.
                </p>
              )}
            </div>
          )}

          {/* Qualified answer → offer the participant a choice:
              ask the mentor (if they haven't yet) or move on. */}
          {existing && existing.adequate !== false && existing.on_topic !== false && (
            <div className="mt-5 pt-4 border-t border-[#C9A961]/15 flex items-center gap-2" data-testid="prompt-next-choice">
              {mentorTurnCount === 0 && (
                <button
                  onClick={() => requestFeedback(existing.id)}
                  disabled={aiLoading === existing.id}
                  className="btn-secondary text-xs flex items-center gap-1.5 flex-1 justify-center disabled:opacity-50"
                  data-testid="prompt-mentor-btn"
                >
                  {aiLoading === existing.id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Sparkles className="w-3.5 h-3.5" />
                  )}
                  Hear from the mentor
                </button>
              )}
              {!allDone && (
                <button
                  onClick={advanceToNextPrompt}
                  className="btn-primary text-xs flex items-center gap-1.5 flex-1 justify-center"
                  data-testid="prompt-next-btn"
                >
                  Next prompt
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Persistent break button for the entire Monday session. Drop full
          NextActionBar pattern when only one action is meaningful. */}
      {!allDone && (
        <SaveBreakBar
          label={`${completedCount} of ${track.journal_prompts.length} prompts complete.`}
          onBreak={onBreak}
        />
      )}
      {allDone && (
        <NextActionBar
          label="All five prompts complete. Ready for Wednesday's assessment?"
          continueLabel="Continue to Wednesday"
          onContinue={onAdvance}
          onBreak={onBreak}
        />
      )}
    </div>
  );
}

/* ---------- Wednesday: Assessment ---------- */
function WednesdaySession({ track, assessment, onSubmit, onComplete, done, onAdvance, onBreak }) {
  const a = track.assessment;
  const [mc, setMc] = useState(Array(a.multiple_choice.length).fill(-1));
  const [tf, setTf] = useState(Array(a.true_false.length).fill(null));
  const [scenario, setScenario] = useState(assessment?.scenario_response || "");
  const [reflection, setReflection] = useState(assessment?.written_reflection || "");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(assessment);

  const submit = async () => {
    if (mc.includes(-1) || tf.includes(null)) {
      toast.error("Please answer all multiple-choice and true/false questions.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/assessments/submit", {
        track_slug: track.slug,
        multiple_choice: mc,
        true_false: tf,
        scenario_response: scenario,
        written_reflection: reflection,
      });
      setResult(data);
      onSubmit(data);
      // Use the backend's reason_message when present — it knows the precise
      // case (score-fail, reflection advisory, full pass). Falls back to the
      // legacy message if an older backend is talking to us.
      const msg = data.reason_message
        || (data.passed
              ? `Submitted. Score: ${data.score_pct}%`
              : `Score ${data.score_pct}% — need ${data.pass_threshold || ASSESSMENT_PASS_PCT}% to count toward Wednesday completion.`);
      if (data.passed) {
        onComplete();
        if (data.reason === "reflection_advisory") {
          toast.message(msg);
        } else {
          toast.success(msg);
        }
      } else {
        toast.warning(msg);
      }
    } catch (e) {
      toast.error("Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="wednesday-session">
      <div className="overline mb-3">Apply & Assess</div>
      <h2
        className="text-3xl text-[#F7F5F0] mb-8"
        style={{ fontFamily: "Cormorant Garamond, serif" }}
      >
        Retention & Application
      </h2>

      {result && (
        <div className="card-bento mb-8 border-[#C9A961]/50" data-testid="assessment-result">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
            <div className="overline">Latest Submission</div>
            {result.passed && (
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#C9A961]/15 text-[#C9A961] text-[11px] tracking-wider"
                data-testid="assessment-passed-badge"
              >
                <Check className="w-3 h-3" /> Wednesday credited
              </span>
            )}
          </div>
          <div className="flex items-end gap-3">
            <span className="text-5xl text-[#C9A961]" style={{ fontFamily: "Cormorant Garamond, serif" }}>
              {result.score_pct}
            </span>
            <span className="text-2xl text-[#C9A961] mb-1">%</span>
          </div>
          <div className="text-sm text-[#D1CFC9] mt-2">
            Multiple Choice: {result.mc_correct}/{result.mc_total} · True/False: {result.tf_correct}/{result.tf_total}
          </div>
          {result.reason === "reflection_advisory" && result.reflection_feedback && (
            <div
              className="mt-4 p-3 rounded-md border border-amber-500/40 bg-[#1A1816]"
              data-testid="reflection-advisory"
            >
              <div className="flex items-center gap-2 mb-1 text-amber-400 text-[11px] uppercase tracking-wider">
                <Sparkles className="w-3 h-3" /> Optional: deepen your reflection
              </div>
              <p className="text-xs text-[#D1CFC9] leading-relaxed">
                {result.reflection_feedback}
              </p>
              <p className="text-[10px] text-[#8A8782] italic mt-2">
                Your Wednesday is already credited — this is advisory only.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Part I */}
      <div className="card-bento mb-6">
        <div className="overline mb-4">Part I — Multiple Choice</div>
        <ol className="space-y-6">
          {a.multiple_choice.map((q, i) => (
            <li key={`mc-${q.q}`} data-testid={`mc-q-${i}`}>
              <div className="text-[#F7F5F0] mb-3">{i + 1}. {q.q}</div>
              <div className="space-y-2">
                {q.options.map((opt, j) => (
                  <label
                    key={`mc-${i}-${opt}`}
                    className={`flex items-center gap-3 p-3 rounded-md border cursor-pointer transition ${
                      mc[i] === j
                        ? "border-[#C9A961] bg-[#C9A961]/10"
                        : "border-[#C9A961]/15 hover:border-[#C9A961]/40"
                    }`}
                  >
                    <input
                      type="radio"
                      name={`mc-${i}`}
                      checked={mc[i] === j}
                      onChange={() => setMc(mc.map((v, idx) => (idx === i ? j : v)))}
                      className="accent-[#C9A961]"
                      data-testid={`mc-${i}-opt-${j}`}
                    />
                    <span className="text-sm text-[#D1CFC9]">{opt}</span>
                  </label>
                ))}
              </div>
            </li>
          ))}
        </ol>
      </div>

      {/* Part II */}
      <div className="card-bento mb-6">
        <div className="overline mb-4">Part II — True / False</div>
        <ol className="space-y-5">
          {a.true_false.map((q, i) => (
            <li key={`tf-${q.q}`} data-testid={`tf-q-${i}`}>
              <div className="text-[#F7F5F0] mb-3">{i + 1}. {q.q}</div>
              <div className="flex gap-3">
                {[true, false].map((val) => (
                  <button
                    key={String(val)}
                    onClick={() => setTf(tf.map((v, idx) => (idx === i ? val : v)))}
                    className={`px-6 py-2 rounded-md border text-sm transition ${
                      tf[i] === val
                        ? "border-[#C9A961] bg-[#C9A961]/10 text-[#C9A961]"
                        : "border-[#C9A961]/20 text-[#D1CFC9] hover:border-[#C9A961]/50"
                    }`}
                    data-testid={`tf-${i}-${val ? "true" : "false"}`}
                  >
                    {val ? "True" : "False"}
                  </button>
                ))}
              </div>
            </li>
          ))}
        </ol>
      </div>

      {/* Part III */}
      <div className="card-bento mb-6">
        <div className="overline mb-4">Part III — Scenario Analysis</div>
        <p className="text-[#D1CFC9] mb-4 leading-relaxed italic">{a.scenario}</p>
        <textarea
          value={scenario}
          onChange={(e) => setScenario(e.target.value)}
          rows={5}
          className="input-tcpi resize-none"
          placeholder="Write your analysis…"
          data-testid="scenario-response"
        />
      </div>

      {/* Part IV */}
      <div className="card-bento mb-6">
        <div className="overline mb-4">Part IV — Written Reflection</div>
        <p className="text-[#D1CFC9] mb-4">{a.written_reflection}</p>
        <textarea
          value={reflection}
          onChange={(e) => setReflection(e.target.value)}
          rows={6}
          className="input-tcpi resize-none"
          placeholder="Your reflection…"
          data-testid="written-reflection"
        />
      </div>

      <button
        onClick={submit}
        disabled={submitting}
        className="btn-primary disabled:opacity-50"
        data-testid="submit-assessment-btn"
      >
        {(() => {
          if (submitting) return "Submitting…";
          return done ? "Resubmit Assessment" : "Submit Assessment";
        })()}
      </button>

      {result && result.passed && (
        <NextActionBar
          label={`Assessment passed at ${result.score_pct}%. Ready for Friday's discussion?`}
          continueLabel="Continue to Friday"
          onContinue={onAdvance}
          onBreak={onBreak}
        />
      )}
      {result && !result.passed && (
        <NextActionBar
          label={`Scored ${result.score_pct}% — need ${result.pass_threshold || ASSESSMENT_PASS_PCT}% to advance. Review your answers and try again.`}
          continueLabel="Save & break"
          breakLabel="Try again"
          onContinue={onBreak}
          onBreak={() => { setResult(null); window.scrollTo({ top: 0, behavior: "smooth" }); }}
        />
      )}
      {!result && (
        <SaveBreakBar
          label="Answer all questions to submit."
          onBreak={onBreak}
        />
      )}
    </div>
  );
}

/* ---------- Friday: Share & Lead ---------- */
function FridaySession({ track, onComplete, done, onAdvance, onBreak, isLastTrack }) {
  const [feed, setFeed] = useState({ posts: [], my_peer_reply_count: 0, min_required: 2 });
  const [loading, setLoading] = useState(true);
  const [composer, setComposer] = useState({ kind: "prompt", target: 0 });
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/friday/${track.slug}`);
      setFeed(data);
    } catch (err) {
      // Soft fail — Friday feed is best-effort; show empty state.
      console.error("Friday: failed to load feed for", track.slug, err);
    } finally {
      setLoading(false);
    }
  }, [track.slug]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async () => {
    if (!draft.trim()) return;
    setPosting(true);
    try {
      const body = composer.kind === "prompt"
        ? { track_slug: track.slug, prompt_index: composer.target, body: draft }
        : { track_slug: track.slug, parent_id: composer.target, body: draft };
      const { data } = await api.post(`/friday/${track.slug}`, body);
      setDraft("");
      if (data.adequate && data.on_topic) {
        toast.success("Posted to the round table.");
      } else {
        toast.warning(data.quality_feedback || "Posted — but please expand for full credit.");
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post.");
    } finally {
      setPosting(false);
    }
  };

  // Group posts by prompt for clean rendering
  const tops = feed.posts.filter((p) => !p.parent_id);
  const repliesByParent = feed.posts.reduce((acc, p) => {
    if (p.parent_id) {
      acc[p.parent_id] = acc[p.parent_id] || [];
      acc[p.parent_id].push(p);
    }
    return acc;
  }, {});
  const meCount = feed.my_peer_reply_count || 0;
  const target = feed.min_required || 2;
  const progressPct = Math.min(100, Math.round((meCount / target) * 100));

  return (
    <div data-testid="friday-session">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="overline">Share & Lead</div>
        <button
          onClick={onBreak}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#C9A961]/40 text-[#C9A961] hover:bg-[#C9A961]/10 transition"
          data-testid="friday-exit-btn"
          title="Save progress and return to your dashboard"
        >
          <LayoutDashboard className="w-3.5 h-3.5" />
          Exit to Dashboard
        </button>
      </div>
      <h2
        className="text-3xl text-[#F7F5F0] mb-3"
        style={{ fontFamily: "Cormorant Garamond, serif" }}
      >
        Round Table Discussion
      </h2>
      <p className="text-[#D1CFC9] max-w-3xl mb-6">
        Listen first. Speak honestly. Lead with humility. Reply to at least <strong>two other
        participants</strong> to earn full Friday credit.
      </p>

      {done && (
        <div
          className="mb-6 rounded-lg border-2 border-[#C9A961] bg-gradient-to-r from-[#C9A961]/15 to-[#C9A961]/5 p-5 flex items-center justify-between gap-4 flex-wrap"
          data-testid="friday-complete-banner"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#C9A961] flex items-center justify-center">
              <Check className="w-5 h-5 text-[#0A0908]" strokeWidth={3} />
            </div>
            <div>
              <div className="text-[#C9A961] font-semibold text-base">
                Friday round-table complete
              </div>
              <div className="text-xs text-[#D1CFC9] mt-0.5">
                You can revisit this discussion anytime — your progress is saved.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={onBreak}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#C9A961] text-[#0A0908] font-semibold hover:bg-[#f4d03f] transition shadow-md"
              data-testid="friday-done-back-btn"
            >
              <LayoutDashboard className="w-4 h-4" />
              Back to Dashboard
            </button>
            {!isLastTrack && (
              <button
                onClick={onAdvance}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-[#C9A961]/60 text-[#C9A961] hover:bg-[#C9A961]/10 transition"
                data-testid="friday-done-next-btn"
              >
                Next track <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Peer-reply progress */}
      <div className="card-bento mb-8" data-testid="friday-progress">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div>
            <div className="overline text-xs mb-1">Your engagement</div>
            <div className="text-[#F7F5F0]">
              You've replied to <span className="text-[#C9A961] font-semibold">{meCount}</span>
              {" "} / {target} peers
              {meCount >= target && <span className="text-[#C9A961]"> · full credit unlocked ✓</span>}
              {meCount === 1 && <span className="text-[#8A8782]"> · 1 more for full credit (currently 50%)</span>}
              {meCount === 0 && <span className="text-[#8A8782]"> · no Friday points until you reply to a peer</span>}
            </div>
          </div>
        </div>
        <div className="h-2 bg-[#1A1816] rounded-full overflow-hidden">
          <div className="h-full bg-[#C9A961] transition-all" style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      {/* Discussion grouped by prompt */}
      <div className="space-y-8 mb-10">
        {track.round_table.map((q, i) => {
          const promptTops = tops.filter((p) => p.prompt_index === i);
          return (
            <div key={`rt-${i}`} className="card-bento" data-testid={`rt-prompt-${i}`}>
              <div className="overline text-xs mb-2">Question {i + 1}</div>
              <p
                className="text-xl text-[#F7F5F0] leading-snug mb-5"
                style={{ fontFamily: "Cormorant Garamond, serif" }}
              >
                {q}
              </p>

              <button
                onClick={() => { setComposer({ kind: "prompt", target: i }); setDraft(""); }}
                className="text-xs text-[#C9A961] hover:underline mb-4"
                data-testid={`compose-prompt-${i}`}
              >
                + Post your answer
              </button>

              {promptTops.length === 0 ? (
                <p className="text-sm text-[#8A8782] italic">No one has answered yet. Be the first.</p>
              ) : (
                <div className="space-y-4">
                  {promptTops.map((p) => (
                    <FridayPost
                      key={p.id}
                      post={p}
                      replies={repliesByParent[p.id] || []}
                      onReply={(parentId) => { setComposer({ kind: "reply", target: parentId }); setDraft(""); }}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Composer */}
      <div className="card-bento sticky bottom-4 z-10" data-testid="friday-composer">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="overline text-xs">
            {composer.kind === "prompt"
              ? `Posting to Question ${composer.target + 1}`
              : "Replying to peer"}
          </div>
          {composer.kind === "reply" && (
            <button
              onClick={() => setComposer({ kind: "prompt", target: 0 })}
              className="text-xs text-[#8A8782] hover:text-[#C9A961]"
            >
              cancel reply
            </button>
          )}
        </div>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={3}
          placeholder="Speak honestly. Listen first. Answer the question to earn credit."
          className="input-tcpi resize-none mb-3"
          data-testid="friday-composer-textarea"
        />
        <div className="flex justify-between items-center flex-wrap gap-3">
          <div className="text-xs text-[#8A8782]">{draft.trim().split(/\s+/).filter(Boolean).length} words</div>
          <button
            onClick={submit}
            disabled={posting || !draft.trim()}
            className="btn-primary text-sm disabled:opacity-50"
            data-testid="friday-post-btn"
          >
            {posting ? "Posting…" : "Post"}
          </button>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap gap-3">
        <button
          onClick={onComplete}
          className={done ? "btn-secondary flex items-center gap-2" : "btn-primary flex items-center gap-2"}
          data-testid="friday-complete-btn"
        >
          {done ? <><Check className="w-4 h-4" /> Friday Complete</> : "Mark Friday Complete"}
        </button>
        <Link to="/forum" className="btn-secondary inline-flex items-center" data-testid="friday-forum-link">
          Open Open Forum
        </Link>
      </div>

      {meCount < target && done && (
        <p className="mt-4 text-sm text-[#8A8782] italic" data-testid="friday-partial-note">
          You've marked this Friday complete. Reply to {target - meCount} more peer{target - meCount === 1 ? "" : "s"} above
          to earn the full {target * 5} Friday points (currently earning partial credit).
        </p>
      )}

      {loading && <p className="text-sm text-[#8A8782] mt-4">Loading the round table…</p>}

      {done && (
        <NextActionBar
          label={isLastTrack
            ? "You've finished the final track of this program. Take a moment."
            : "Friday complete. Ready for the next track?"}
          continueLabel={isLastTrack ? "Return to Dashboard" : "Continue to next track"}
          onContinue={onAdvance}
          onBreak={onBreak}
        />
      )}
      {!done && (
        <SaveBreakBar
          label={`${feed.my_peer_reply_count || 0} of ${feed.min_required || 2} peer replies posted.`}
          onBreak={onBreak}
        />
      )}
    </div>
  );
}

function FridayPost({ post, replies, onReply }) {
  const isMine = post._mine; // backend doesn't set this; UI determines via author_id below
  const dim = post.adequate === false || post.on_topic === false;
  return (
    <div className="border border-[#C9A961]/15 rounded-md p-4 bg-[#1A1816]" data-testid={`friday-post-${post.id}`}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2 text-xs">
          <span className="text-[#C9A961] font-semibold">{post.author_name}</span>
          {post.author_role === "facilitator" && (
            <span className="px-2 py-0.5 rounded-full bg-[#C9A961]/15 text-[#C9A961] text-[10px] tracking-wider">FACILITATOR</span>
          )}
          {post.author_anonymous && <span className="text-[#8A8782]">· anonymous</span>}
          <span className="text-[#8A8782]">· {new Date(post.created_at).toLocaleString()}</span>
        </div>
        <button
          onClick={() => onReply(post.id)}
          className="text-xs text-[#C9A961] hover:underline"
          data-testid={`reply-btn-${post.id}`}
        >
          Reply
        </button>
      </div>
      <p className={`text-sm whitespace-pre-wrap leading-relaxed ${dim ? "text-[#8A8782] italic" : "text-[#D1CFC9]"}`}>
        {post.body}
      </p>

      {replies.length > 0 && (
        <div className="mt-4 pl-4 border-l-2 border-[#C9A961]/20 space-y-3">
          {replies.map((r) => (
            <div key={r.id} className="text-sm" data-testid={`friday-reply-${r.id}`}>
              <div className="flex items-center gap-2 text-xs mb-1">
                <span className="text-[#C9A961] font-semibold">{r.author_name}</span>
                {r.author_role === "facilitator" && (
                  <span className="px-1.5 py-0.5 rounded-full bg-[#C9A961]/15 text-[#C9A961] text-[10px]">FACILITATOR</span>
                )}
                <span className="text-[#8A8782]">· {new Date(r.created_at).toLocaleString()}</span>
              </div>
              <p className={`whitespace-pre-wrap leading-relaxed ${r.adequate === false ? "text-[#8A8782] italic" : "text-[#D1CFC9]"}`}>{r.body}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
